import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Typography, Image, Input, Empty } from 'antd';
import { BookOutlined } from '@ant-design/icons';
import axios from 'axios';
import { manualData } from './manualData';
import './UserManual.css';

const { Sider, Content } = Layout;
const { Title, Paragraph } = Typography;
const { Search } = Input;

const UserManual = () => {
  const [userRole, setUserRole] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchUserRole = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserRole(response.data.role);
        filterManualByRole(response.data.role);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [API_URL]);

  const filterManualByRole = (role) => {
    let data = [];
    
    if (role === 'superadmin' || role === 'admin') {
      // Superadmin and Admin see all sections
      data = manualData;
    } else if (role === 'teacher') {
      // Teacher only sees Teacher section
      data = manualData.filter(item => item.category === 'Teacher');
    } else if (role === 'student') {
      // Student only sees Student section
      data = manualData.filter(item => item.category === 'Student');
    }
    
    setFilteredData(data);
    
    // Set default selected topic to the first topic available
    if (data.length > 0 && data[0].sections.length > 0 && data[0].sections[0].topics.length > 0) {
      setSelectedTopic(data[0].sections[0].topics[0]);
    }
  };

  // Filter topics by search
  const getSearchFilteredData = () => {
    if (!searchValue) return filteredData;

    return filteredData.map(cat => ({
      ...cat,
      sections: cat.sections.map(section => ({
        ...section,
        topics: section.topics.filter(t =>
          t.title.toLowerCase().includes(searchValue.toLowerCase())
        ),
      })).filter(section => section.topics.length > 0),
    })).filter(cat => cat.sections.length > 0);
  };

  const searchFilteredData = getSearchFilteredData();

  // Generate menu items with three-layer structure
  const generateMenuItems = () => {
    return searchFilteredData.map(cat => ({
      key: cat.category,
      label: cat.category,
      icon: <BookOutlined />,
      children: cat.sections.map(section => ({
        key: `${cat.category}-${section.section}`,
        label: section.section,
        children: section.topics.map(topic => ({
          key: topic.id,
          label: topic.title,
          onClick: () => setSelectedTopic(topic),
        })),
      })),
    }));
  };

  return (
    <Layout style={{ minHeight: '100vh', background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.03) 0%, rgba(244, 248, 187, 0.03) 100%)' }}>
      <Sider
        width={280}
        style={{
          background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
          borderRight: '2px solid rgba(125, 220, 192, 0.2)',
          padding: '20px 0',
          boxShadow: '4px 0 12px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ 
          padding: '0 16px 20px',
          borderBottom: '2px solid rgba(125, 220, 192, 0.2)',
          marginBottom: '16px',
        }}>
          <Title level={4} style={{ 
            margin: '0 0 12px 0', 
            color: '#2d3748',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontWeight: 700,
          }}>
            Kease User Manual
          </Title>
          <Search
            placeholder="Search topics..."
            onChange={e => setSearchValue(e.target.value)}
            allowClear
            style={{ marginBottom: 0 }}
          />
        </div>
        
        {searchFilteredData.length > 0 ? (
          <Menu
            mode="inline"
            defaultOpenKeys={searchFilteredData.length > 0 ? [searchFilteredData[0].category] : []}
            items={generateMenuItems()}
            style={{ border: 'none' }}
          />
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
            No topics found
          </div>
        )}
      </Sider>

      <Content style={{ 
        padding: '32px 48px', 
        overflowY: 'auto', 
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 252, 255, 0.95) 100%)',
      }}>
        {selectedTopic ? (
          <Card
            bordered={false}
            style={{
              boxShadow: '0 4px 20px rgba(125, 220, 192, 0.15), 0 2px 8px rgba(0, 0, 0, 0.08)',
              borderRadius: '16px',
              border: '1px solid rgba(125, 220, 192, 0.15)',
              background: 'white',
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{
              background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.08) 0%, rgba(244, 248, 187, 0.08) 100%)',
              padding: '20px 24px',
              marginBottom: '24px',
              borderRadius: '12px',
              border: '1px solid rgba(125, 220, 192, 0.2)',
            }}>
              <Title level={3} style={{ 
                color: '#2d3748', 
                marginBottom: '0',
                fontSize: '26px',
                fontWeight: 700,
              }}>
                {selectedTopic.title}
              </Title>
            </div>
            
            {selectedTopic.image && (
              <div style={{ 
                margin: '24px 0',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(125, 220, 192, 0.03) 0%, rgba(244, 248, 187, 0.03) 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(125, 220, 192, 0.1)',
              }}>
                <Image
                  src={selectedTopic.image}
                  alt={selectedTopic.title}
                  style={{ 
                    borderRadius: 8, 
                    maxWidth: '100%',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                />
              </div>
            )}
            
            <Paragraph style={{ 
              fontSize: '15px', 
              lineHeight: '1.9', 
              color: '#4a5568',
              padding: '0 4px',
            }}>
              <div
                style={{
                  wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{
                  __html: selectedTopic.description.replace(/\n/g, '<br/>'),
                }}
              />
            </Paragraph>
          </Card>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div style={{ 
                  fontSize: '18px', 
                  color: '#718096',
                  marginBottom: '8px',
                  fontWeight: 600,
                }}>
                  No Topic Selected
                </div>
                <span style={{ color: '#a0aec0', fontSize: '14px' }}>
                  Select a topic from the left sidebar to view details
                </span>
              </div>
            }
            style={{ 
              marginTop: '120px',
              padding: '40px',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(125, 220, 192, 0.08)',
            }}
          />
        )}
      </Content>
    </Layout>
  );
};

export default UserManual;